import type { FocusedReference } from "./focused-references.js";
import type { DevArtifactReference } from './dev-artifacts.js';
/**
 * Aquaduck shared document model.
 *
 * There is exactly ONE Yjs document for the whole workspace (no projects /
 * spaces yet). Every client (humans AND the Aquaduck agent) syncs the same doc
 * over the websocket at `/sync` and persists locally with y-indexeddb for
 * offline-first editing. All product state lives in this doc; the server only
 * persists it (AlloyDB) and stores blobs (GCS).
 *
 * Top-level shared types (see `DocKeys`):
 *   users      Y.Map<UserRecord>          key = userId
 *   objects    Y.Map<Y.Map<unknown>>      key = objectId   (see ObjectFields)
 *   present    Y.Map<Y.Map<unknown>>      key = presentId  (see PresentFields)
 *   chat       Y.Array<ChatMessage>       append-only
 *   comments   Y.Map<Comment>             key = commentId
 *   highlights Y.Map<Highlight>           key = highlightId
 *
 * Rules that every lane must respect:
 *  - Objects and present entries are Y.Maps so concurrent field edits merge.
 *    Use the `ObjectFields` / `PresentFields` names below; do not invent keys.
 *  - Text objects store their rich text as a Y.XmlFragment under
 *    `ObjectFields.content` (bound to TipTap via y-prosemirror). Everything
 *    else in a text object is plain fields.
 *  - Chat messages, comments and highlights are immutable plain JSON values
 *    (replace the whole value to edit; they are small).
 *  - The agent is a normal user: it has a UserRecord with `isAgent: true`,
 *    posts ChatMessages with its own userId, and publishes awareness like a
 *    human. Nothing in the UI may special-case it except its badge/logo.
 */

import type * as Y from "yjs";
import type { BlobUploadResponse } from "./api.js";
import type { ImageVersion } from "./images.js";

export const DOC_NAME = "aquaduck-main";

export const DocKeys = {
  users: "users",
  objects: "objects",
  present: "present",
  chat: "chat",
  focusChats: "focusChats", // Y.Map<Y.Array<ChatMessage>>, key = objectId; server creates arrays
  focusSessions: "focusSessions", // Y.Map<FocusSession>, key = sessionId
  emoji: "emoji", // Y.Map<CustomEmoji>, key = shortcode
  messageReactions: "messageReactions", // Y.Map<MessageReaction>, key = JSON [messageId, emoji, userId]
  comments: "comments",
  highlights: "highlights",
  skills: "skills", // Y.Map<Skill>, immutable JSON values keyed by skill id
  /** Y.Map<WorkspaceStyle>-like map: workspace-wide style applied by Preview (see WorkspaceStyleFields). */
  style: "style",
  /** Y.Map<unknown> holding the per-workspace Aquaduck agent configuration (see AgentSettingsFields). */
  agentSettings: "agentSettings",
  workspaceAgents: "workspaceAgents", // Registry of additional and removed Aquaduck identities
  agentRuns: "agentRuns", // Y.Map<AgentRunRecord>, task progress/outcomes keyed by task id
} as const;

/** Keys inside the `style` Y.Map. Values are plain JSON. Preview applies them; blocks may override. */
export const WorkspaceStyleFields = {
  background: "background", // css color
  accent: "accent", // css color
  fontFamily: "fontFamily", // css font stack
  title: "title", // Preview page title
} as const;
export function getStyle(doc: Y.Doc): Y.Map<unknown> {
  return doc.getMap<unknown>(DocKeys.style);
}

/** The Aquaduck agent identity. Stable id so mentions/links keep working. */
export const AGENT_USER = {
  id: "aquaduck",
  name: "Aquaduck",
  colorId: "duck",
  avatarUrl: "/aquaduck_logo.png",
} as const;

/** How the agent is mentioned in chat: `@Aquaduck` (case-insensitive). */
export const AGENT_MENTION = "@Aquaduck";

// ---------------------------------------------------------------- users

export interface UserRecord {
  id: string;
  name: string;
  /** A preset id, six-digit custom hex, or a reserved agent color id. */
  colorId: string;
  /** Cropped profile photo in the existing blob store; absent for initials. */
  avatarUrl?: string;
  isAgent: boolean;
  joinedAt: number; // epoch ms
  /** Updated occasionally (every minute or so) while connected. */
  lastSeenAt: number;
}

// -------------------------------------------------------------- objects

export type ObjectType = "text" | "image" | "pdf" | "file" | "html" | "video" | "audio" | "frame" | "presentation" | "template" | "font" | "palette";

/** Runtime guard for persisted data and writes from untyped/stale clients. */
export function isObjectType(value: unknown): value is ObjectType {
  return typeof value === "string" && Object.hasOwn(DEFAULT_OBJECT_SIZE, value);
}

/**
 * Field names inside an object's Y.Map. Values are plain JSON except
 * `content` (Y.XmlFragment, text objects) and `state` (nested Y.Map).
 */
export const ObjectFields = {
  id: "id",
  type: "type", // ObjectType
  x: "x", // canvas coords (world units == css px at zoom 1)
  y: "y",
  w: "w",
  h: "h",
  z: "z", // stacking order; larger is on top
  /** Absolute world coordinates are retained. Frames cannot be children. */
  parentId: "parentId", // optional frame id; absent means canvas root
  collapsed: "collapsed", // frame only; children remain stored
  rotation: "rotation", // degrees, optional, default 0
  title: "title", // string, shown in object chrome
  createdBy: "createdBy", // userId
  createdAt: "createdAt", // epoch ms
  updatedAt: "updatedAt",
  color: "color", // optional hex accent (border/background tint)
  /** Provenance: userId of the last editor (set alongside updatedAt). */
  updatedBy: "updatedBy",
  /**
   * Soft delete ("Trash"). A deleted object keeps its Y.Map and content; UIs hide it,
   * the Trash panel can restore it (delete both fields), and only an explicit purge
   * removes the Y.Map. Never hard-delete from the UI or agent tools.
   */
  deletedAt: "deletedAt", // epoch ms
  deletedBy: "deletedBy", // userId
  /** Set once a human has looked at an agent-created object (clears the "new from agent" badge). */
  reviewedAt: "reviewedAt",
  /**
   * Agent placeholder: set while an agent is still producing this object (shows a loading card);
   * cleared when the content lands. Value: epoch ms. `pendingBy` = agent userId.
   */
  pendingAt: "pendingAt",
  pendingBy: "pendingBy",
  /**
   * Synced interactive state for html objects: a nested Y.Map<string, JSON> that the sandboxed
   * document reads/writes through the bridge in sandbox.ts. `synced` (boolean) says whether the
   * object currently mirrors state across viewers (the "Synced" badge toggles it).
   */
  state: "state",
  synced: "synced",
  // text objects
  content: "content", // Y.XmlFragment (TipTap/ProseMirror document)
  // image / pdf / file objects
  blobUrl: "blobUrl", // `/api/blobs/<key>` served by our server (GCS-backed)
  mime: "mime",
  fileName: "fileName",
  sizeBytes: "sizeBytes",
  imageVersions: "imageVersions", // immutable ImageVersion[]; appended by the image editing API
  // html objects (vibe-coded, agent-made or pasted). Rendered in a sandboxed iframe.
  html: "html", // string: a complete self-contained HTML document (html objects and presentation packages)
  // Presentations (see shared/src/presentation.ts): inline static package during Phase 0.
  manifest: "manifest", // string: JSON PresentationManifest
  sourceDigest: "sourceDigest", // string: sha-256 hex of `html`; expected-source check for every package write
  claims: "claims", // string: JSON PresentationClaim[]
  sources: "sources", // string: JSON PresentationSource[]
  inputSnapshot: "inputSnapshot", // JSON: capturedAt and ordered immutable input content for presentation creation
  // Multi-file projects (see shared/src/project.ts): JSON ProjectSource for html objects and presentations. When present,
  // `sourceDigest` covers canonicalProjectJson(project) and `html` mirrors files["index.html"] for legacy readers.
  project: "project",
  // Last runtime errors captured by the client preview (JSON ProjectDiagnostic[] with kind "runtime"), for agent diagnostics.
  runtimeErrors: "runtimeErrors",
  // Style nodes (see shared/src/styles.ts): JSON FontSpec / PaletteSpec; templates are frame-like containers (parentId).
  font: "font",
  palette: "palette",
  // Presentations generated with style guidance: JSON StyleSnapshot and JSON FontAssetRef[] (font bytes stay in blobs).
  styleSnapshot: "styleSnapshot",
  fontAssets: "fontAssets",
  prompt: "prompt", // string: the request that produced it (for context/regeneration)
} as const;

/** Plain-JSON view of an object (what you get after reading the Y.Map). */
export interface CanvasObject {
  id: string;
  type: ObjectType;
  x: number;
  y: number;
  w: number;
  h: number;
  z: number;
  parentId?: string;
  collapsed?: boolean;
  rotation?: number;
  title?: string;
  createdBy: string;
  createdAt: number;
  updatedAt?: number;
  updatedBy?: string;
  deletedAt?: number;
  deletedBy?: string;
  reviewedAt?: number;
  pendingAt?: number;
  pendingBy?: string;
  synced?: boolean;
  color?: string;
  blobUrl?: string;
  mime?: string;
  fileName?: string;
  sizeBytes?: number;
  imageVersions?: ImageVersion[];
  html?: string;
  manifest?: string;
  sourceDigest?: string;
  claims?: string;
  sources?: string;
  inputSnapshot?: string;
  project?: string;
  runtimeErrors?: string;
  font?: string;
  palette?: string;
  styleSnapshot?: string;
  fontAssets?: string;
  prompt?: string;
}

export const DEFAULT_OBJECT_SIZE: Record<ObjectType, { w: number; h: number }> = {
  text: { w: 420, h: 260 },
  image: { w: 480, h: 320 },
  pdf: { w: 520, h: 680 },
  file: { w: 280, h: 96 },
  html: { w: 640, h: 420 },
  video: { w: 640, h: 360 },
  audio: { w: 420, h: 96 },
  frame: { w: 720, h: 520 },
  presentation: { w: 800, h: 500 },
  template: { w: 720, h: 520 },
  font: { w: 320, h: 160 },
  palette: { w: 320, h: 120 },
};

// -------------------------------------------------------------- present

/**
 * The Present view is an ordered, vertically scrolling list of blocks. Each
 * block is a reference to a collaborate-canvas object (or a text highlight)
 * plus optional per-block overrides. Deleting a present block never deletes
 * the source object.
 */
export const PresentFields = {
  id: "id",
  kind: "kind", // "object" | "highlight"
  sourceId: "sourceId", // objectId (for kind=object) or highlightId (for kind=highlight)
  /** Fractional ordering key (string, lexicographically sorted) so reorders merge well. */
  order: "order",
  createdBy: "createdBy",
  createdAt: "createdAt",
  // Overrides. When absent, fall back to the source object's value.
  w: "w",
  h: "h",
  color: "color",
  title: "title",
  /** For kind=object&type=text or kind=highlight: optional replacement plain text/markdown. */
  text: "text",
  /** Distinguishes a user override equal to a highlight's initial snapshot. */
  textIsOverride: "textIsOverride",
  /** Preview: render this block at full page width instead of inside a container. */
  fullWidth: "fullWidth",
} as const;

export type PresentKind = "object" | "highlight";

export interface PresentBlock {
  id: string;
  kind: PresentKind;
  sourceId: string;
  order: string;
  createdBy: string;
  createdAt: number;
  w?: number;
  h?: number;
  color?: string;
  title?: string;
  text?: string;
  textIsOverride?: boolean;
  fullWidth?: boolean;
}

// ----------------------------------------------------------------- chat

export interface CustomEmoji {
  id: string;
  shortcode: string; // including colons, e.g. :team_duck:
  url: string; // same-origin blob API URL
  createdBy: string;
  createdAt: number;
}

/** Server creates sessions from awareness; clients may set endedAt via End session. */
export interface FocusSession {
  id: string;
  objectId: string;
  startedAt: number;
  startIndex: number;
  endedAt?: number;
  endIndex?: number;
  summary?: "pending" | "posted" | "skipped" | "failed";
}
export function getFocusChats(doc: Y.Doc): Y.Map<Y.Array<ChatMessage>> {
  return doc.getMap<Y.Array<ChatMessage>>(DocKeys.focusChats);
}
export function getFocusSessions(doc: Y.Doc): Y.Map<FocusSession> {
  return doc.getMap<FocusSession>(DocKeys.focusSessions);
}

export interface ChatMessage {
  /** Worker CTAs on dispatch and summary messages; status lives in agentRuns. */
  tasks?: { id: string; title: string }[];
  /** Human-created presentation reservation to adopt; never reserve a duplicate. */
  target?: { objectId: string };
  /** Main-chat summary link, reopening the object's focused history. */
  focusSession?: { objectId: string; sessionId: string };

  id: string;
  userId: string;
  text: string; // markdown-ish plain text; empty for attachment-only messages
  /** Ordered uploaded images and text files. Only blob API metadata, never inline bytes or draft URLs. */
  attachments?: BlobUploadResponse[];
  /** Live objects in the development-only Device Cow workspace. */
  artifacts?: DevArtifactReference[];
  createdAt: number;
  /** Object ids referenced by this message (rendered as focus links). */
  refs?: string[];
  /** Highlighted excerpts take priority; refs retain access to their whole source objects. */
  focusedRefs?: FocusedReference[];
  /** Present block ids referenced by this message. */
  presentRefs?: string[];
  /** User ids mentioned with `@`. Includes AGENT_USER.id when the agent is pinged. */
  mentions?: string[];
  /** Thread root this message replies to (threads, 2026-09-15): always the ROOT message id, never a reply. Roots have no replyTo. */
  replyTo?: string;
}

// ------------------------------------------------------------- comments

/** A durable bubble comment anchored on the collaborate canvas. */
export interface Comment {
  id: string;
  /** When set, (x, y) are relative to the object's top-left and move with it. */
  objectId: string | null;
  x: number;
  y: number;
  text: string;
  userId: string;
  createdAt: number;
  resolved?: boolean;
  /** Threading: id of the root comment this one replies to (root comments omit it). */
  replyTo?: string;
  /** User ids mentioned with `@` (AGENT_USER.id / DEVCOW_USER.id when an agent is asked to act). */
  mentions?: string[];
  /** Object ids referenced with #id (or legacy [[obj:id]]) in the text. */
  refs?: string[];
  /** Highlighted excerpts take priority; refs retain access to their whole source objects. */
  focusedRefs?: FocusedReference[];
  /** Optional file attachments, same shape and blob store as ChatMessage.attachments. */
  attachments?: BlobUploadResponse[];
  /** Explicit Device Cow mention posted from a development-mode comment composer. */
  developmentRequest?: true;
}

// ----------------------------------------------------------- highlights

/**
 * A durable highlight inside a text object that was forwarded to Present.
 * `from`/`to` are Y.RelativePosition encoded via
 * `Y.relativePositionToJSON` so they survive concurrent edits.
 */
export interface Highlight {
  id: string;
  objectId: string;
  from: unknown; // Y.RelativePosition JSON
  to: unknown; // Y.RelativePosition JSON
  /** The present block created from this highlight. */
  presentId: string;
  createdBy: string;
  createdAt: number;
  /** Plain-text snapshot of the highlighted span at creation (fallback rendering). */
  snapshot: string;
}

// --------------------------------------------------------------- skills

/** Team-authored agent instructions. Replace the whole value when editing. */
export interface Skill {
  id: string;
  title: string;
  markdown: string;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
  enabled: boolean;
  /**
   * Set when this skill was seeded from a shipped default (`agents/skills/<name>/SKILL.md`).
   * Built-in skills can be edited and disabled but not deleted; "Reset to default" restores
   * the shipped markdown. Team-authored skills omit this field.
   */
  builtin?: string;
}
export function getSkills(doc: Y.Doc, agentId: string = AGENT_USER.id): Y.Map<Skill> {
  return doc.getMap<Skill>(agentId === AGENT_USER.id ? DocKeys.skills : `agentSkills:${agentId}`);
}

// ---------------------------------------------------------- agent settings

/**
 * Per-workspace Aquaduck agent configuration, edited from the presence menu
 * (AI user → Configure). Absent fields mean "server default". Field names
 * inside the `agentSettings` Y.Map; values are plain JSON.
 */
export const AgentSettingsFields = {
  name: "name",
  /** Model id chosen from `GET /api/agent/models`; absent → the server's `OPENAI_MODEL`. */
  model: "model",
  /** Free-text instructions appended to the agent's system prompt for this workspace (≤ 8,000 chars). */
  instructions: "instructions",
  /** When the agent answers human chat messages: "mention" (default, explicit @Aquaduck), "classifier" (a cheap model call decides), "always". */
  trigger: "trigger",
  updatedAt: "updatedAt",
  updatedBy: "updatedBy", // userId
} as const;
export type AgentTrigger = "mention" | "classifier" | "always";
export const AGENT_TRIGGERS: readonly AgentTrigger[] = ["mention", "classifier", "always"];
export const AGENT_INSTRUCTIONS_MAX_CHARS = 8_000;
export interface AgentSettings {
  name?: string;
  model?: string;
  instructions?: string;
  trigger?: AgentTrigger;
  updatedAt?: number;
  updatedBy?: string;
}
export function getAgentSettingsMap(doc: Y.Doc, agentId: string = AGENT_USER.id): Y.Map<unknown> {
  return doc.getMap<unknown>(agentId === AGENT_USER.id ? DocKeys.agentSettings : `agentSettings:${agentId}`);
}
/** Read the plain settings object (unknown/invalid values are dropped). */
export function readAgentSettings(doc: Y.Doc, agentId: string = AGENT_USER.id): AgentSettings {
  const m = getAgentSettingsMap(doc, agentId);
  const out: AgentSettings = {};
  const name = m.get(AgentSettingsFields.name); if (typeof name === "string" && name.trim()) out.name = name.trim().slice(0, 80);
  const model = m.get(AgentSettingsFields.model); if (typeof model === "string" && model) out.model = model;
  const instructions = m.get(AgentSettingsFields.instructions); if (typeof instructions === "string") out.instructions = instructions.slice(0, AGENT_INSTRUCTIONS_MAX_CHARS);
  const trigger = m.get(AgentSettingsFields.trigger); if (typeof trigger === "string" && (AGENT_TRIGGERS as readonly string[]).includes(trigger)) out.trigger = trigger as AgentTrigger;
  const updatedAt = m.get(AgentSettingsFields.updatedAt); if (typeof updatedAt === "number") out.updatedAt = updatedAt;
  const updatedBy = m.get(AgentSettingsFields.updatedBy); if (typeof updatedBy === "string") out.updatedBy = updatedBy;
  return out;
}

// ------------------------------------------------------------- helpers

export function getUsers(doc: Y.Doc): Y.Map<UserRecord> {
  return doc.getMap<UserRecord>(DocKeys.users);
}
export function getObjects(doc: Y.Doc): Y.Map<Y.Map<unknown>> {
  return doc.getMap<Y.Map<unknown>>(DocKeys.objects);
}
export function getPresent(doc: Y.Doc): Y.Map<Y.Map<unknown>> {
  return doc.getMap<Y.Map<unknown>>(DocKeys.present);
}
export function getEmoji(doc: Y.Doc): Y.Map<CustomEmoji> {
  return doc.getMap<CustomEmoji>(DocKeys.emoji);
}
export function getChat(doc: Y.Doc): Y.Array<ChatMessage> {
  return doc.getArray<ChatMessage>(DocKeys.chat);
}
export function getComments(doc: Y.Doc): Y.Map<Comment> {
  return doc.getMap<Comment>(DocKeys.comments);
}
export function getHighlights(doc: Y.Doc): Y.Map<Highlight> {
  return doc.getMap<Highlight>(DocKeys.highlights);
}

/** Read an object's plain fields (excludes rich text content). */
export function readObject(m: Y.Map<unknown>): CanvasObject {
  const o: Record<string, unknown> = {};
  m.forEach((v, k) => {
    if (k !== ObjectFields.content) o[k] = v;
  });
  return o as unknown as CanvasObject;
}

/** True when the object is in the Trash (soft-deleted). */
export function isDeleted(o: { deletedAt?: number }): boolean {
  return typeof o.deletedAt === "number";
}
/** Live (non-trashed) objects as plain records, optionally including trashed ones. */
export function listObjects(doc: Y.Doc, opts: { includeDeleted?: boolean } = {}): CanvasObject[] {
  const out: CanvasObject[] = [];
  getObjects(doc).forEach((m) => {
    if (!m || typeof m.get !== "function" || !isObjectType(m.get(ObjectFields.type))) return;
    const o = readObject(m);
    if (opts.includeDeleted || !isDeleted(o)) out.push(o);
  });
  return out;
}

/** Live, non-frame children in reading order (top to bottom, then left to right). */
export function childrenOf(doc: Y.Doc, frameId: string): CanvasObject[] {
  return listObjects(doc).filter(o => o.type !== "frame" && o.parentId === frameId)
    .sort((a, b) => a.y - b.y || a.x - b.x || a.z - b.z || a.id.localeCompare(b.id));
}

export function readPresentBlock(m: Y.Map<unknown>): PresentBlock {
  const o: Record<string, unknown> = {};
  m.forEach((v, k) => (o[k] = v));
  return o as unknown as PresentBlock;
}

/** Sorted present blocks (by fractional `order`, then createdAt). */
export function listPresentBlocks(doc: Y.Doc): PresentBlock[] {
  const out: PresentBlock[] = [];
  getPresent(doc).forEach((m) => out.push(readPresentBlock(m)));
  return out.sort((a, b) => (a.order < b.order ? -1 : a.order > b.order ? 1 : a.createdAt - b.createdAt));
}

/**
 * Fractional index helper: returns a string strictly between `a` and `b`
 * (either may be undefined for the ends). Simple base-36 midpoint; fine for
 * a prototype.
 */
export function orderBetween(a?: string, b?: string): string {
  const A = a ?? "";
  const B = b ?? "";
  const digits = "0123456789abcdefghijklmnopqrstuvwxyz";
  let i = 0;
  let out = "";
  for (;;) {
    const ca = i < A.length ? digits.indexOf(A[i]!) : 0;
    const cb = i < B.length ? digits.indexOf(B[i]!) : digits.length;
    if (cb - ca > 1) {
      out += digits[Math.floor((ca + cb) / 2)];
      return out;
    }
    out += digits[ca];
    i++;
    if (i > 64) return out + "i";
  }
}

/** Bounded diagnostic history, immutable JSON values. Times are epoch milliseconds. */
export type AgentFailureKind = "timeout" | "model_incomplete" | "api" | "tool" | "deadline" | "aborted" | "incomplete" | "unknown";
export interface AgentRunRecord {
  /** Absent on legacy outcomes authored by the original Aquaduck. */
  agentId?: string;
  id: string;
  title: string;
  requestId: string;
  status: "running" | "completed" | "failed";
  cause?: string;
  causeKind?: AgentFailureKind;
  startedAt: number;
  finishedAt?: number;
  /** Created/reserved or filled objects, ordered by their latest creation/fill. */
  objectIds?: string[];
  /** Last surviving completed object from objectIds. */
  lastObjectId?: string;
  toolCalls?: string[];
}
export function getAgentRuns(doc: Y.Doc): Y.Map<AgentRunRecord> {
  return doc.getMap<AgentRunRecord>(DocKeys.agentRuns);
}

/** Content-free provider diagnostics carried through agent usage reports. */
export interface ProviderUsage {
  provider: "vertex"; model: string; requestId: string; agentId: string;
  input: number; output: number; thoughts?: number; durationMs: number; attempts: number;
  outcome: "ok" | "error"; failureCategory?: string;
}
